import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  User,
  Mail,
  Phone,
  MapPin,
  AlertTriangle,
  Badge,
  Heart,
  FileText,
  Shield,
  Users,
  Cake,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { apiClient } from '../utils/api';
import {
  IdPhotoGallery,
  DocumentThumbnail,
} from '../components/camera/PhotoGallery';
import { listClientIdPhotos } from '../services/clientIdPhotosApi';
import type { IdPhoto } from '../components/camera/types';
import { isMinor as isMinorFromBirthDate } from '@digitaltattoo/shared-utils';

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string | null;
  isMinor: boolean | null;

  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'VIP';

  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };

  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
    email: string;
  };

  preferences: {
    communicationMethod: string;

    reminderSettings: {
      enabled: boolean;
      daysBefore: number[];
      methods: string[];
    };

    privacySettings: {
      allowPhotography: boolean;
      allowGalleryUse: boolean;
      allowTestimonials: boolean;
      allowMarketing: boolean;
    };
  };

  identityDocument?: {
    type: string;
    number: string;
    issueDate?: string;
    expiryDate?: string;
    issuePlace?: string;
    issuingAuthority?: string;
    fileUrl?: string;
    fileName?: string;
    fileMimeType?: string;
  };

  legalGuardian?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    relationshipToMinor: string;
    relationshipDetails?: string;

    identityDocument?: {
      type: string;
      number: string;
      fileUrl?: string;
      fileName?: string;
      fileMimeType?: string;
    };
  };

  consentLogs?: Array<{
    id: string;
    consentType: string;
    consentGiven: boolean;
    timestamp: string;
  }>;

  createdAt: string;
  updatedAt: string;
}

export function ClientViewPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Photos des documents d'identité
  const [idPhotos, setIdPhotos] = useState<IdPhoto[]>([]);

  useEffect(() => {
    if (id) {
      fetchClient(id);
      loadIdPhotos(id);
    }
  }, [id]);

  const fetchClient = async (clientId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient(`/api/admin/clients/${clientId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch client');
      }

      const data = await response.json();

      if (data.success) {
        setClient(data.data.client);
      } else {
        setError(data.message || 'Failed to load client');
      }
    } catch (error) {
      console.error('Fetch client error:', error);
      setError('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const loadIdPhotos = async (clientId: string) => {
    try {
      const photos = await listClientIdPhotos(clientId);
      setIdPhotos(photos);
    } catch {
      // Les photos ne bloquent pas l'affichage du profil
    }
  };

  const handleEdit = () => {
    if (client) {
      navigate(`/clients/${client.id}/edit`);
    }
  };

  const handleBackToList = () => {
    navigate('/clients');
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) {
      return 'Non renseigné';
    }

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return 'Non renseigné';
    }

    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const calculateAge = (dateOfBirth?: string | null) => {
    if (!dateOfBirth) {
      return null;
    }

    const today = new Date();
    const birthDate = new Date(dateOfBirth);

    if (Number.isNaN(birthDate.getTime())) {
      return null;
    }

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      ACTIVE: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: 'Actif',
      },
      INACTIVE: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        label: 'Inactif',
      },
      SUSPENDED: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        label: 'Suspendu',
      },
      VIP: {
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        label: 'VIP',
      },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] ||
      statusConfig.ACTIVE;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    );
  };

  const getDocumentTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      passport: 'Passeport',
      national_id: "Carte d'identité",
      driving_license: 'Permis de conduire',
      birth_certificate: 'Acte de naissance',
    };

    return types[type] || type;
  };

  const getConsentTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      data_processing: 'Traitement des données',
      photography: 'Photographie',
      marketing: 'Marketing',
      minor_consent: 'Consentement mineur',
    };

    return types[type] || type;
  };

  const getGuardianRelationshipLabel = (
    relationshipToMinor?: string,
    relationshipDetails?: string,
  ) => {
    let label = 'Autre';

    if (relationshipToMinor === 'parent') {
      label = 'Parent';
    } else if (relationshipToMinor === 'legal_guardian') {
      label = 'Tuteur légal';
    }

    if (relationshipDetails) {
      return `${label} (${relationshipDetails})`;
    }

    return label;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />

        <span className="ml-2 text-gray-600">
          Chargement du client...
        </span>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />

            <span className="text-red-800 font-medium">
              {error || 'Client non trouvé'}
            </span>
          </div>
        </div>

        <button
          onClick={handleBackToList}
          className="mt-4 btn-secondary"
        >
          Retour à la liste des clients
        </button>
      </div>
    );
  }

  const clientAge = calculateAge(client.dateOfBirth);
  const clientIsMinor = client.dateOfBirth
    ? isMinorFromBirthDate(client.dateOfBirth)
    : client.isMinor;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* ========================================================= */}
      {/* HEADER */}
      {/* ========================================================= */}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />

            <span className="hidden sm:inline">
              Retour à la liste
            </span>
          </button>

          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex flex-wrap items-center gap-2">
              <span>
                {client.firstName} {client.lastName}
              </span>

              {clientIsMinor === true && (
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 border border-amber-200">
                  Mineur
                </span>
              )}

              <HelpIcon
                docPath="05-gestion-clients#05-5-consultation"
                tooltip="Documentation : Consultation d'un client"
              />
            </h1>

            <p className="text-textSecondary">
              Profil du client
            </p>
          </div>
        </div>

        <button
          onClick={handleEdit}
          className="btn-primary flex items-center justify-center space-x-2"
        >
          <Edit className="w-4 h-4" />

          <span>Modifier</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-2 space-y-8">
           <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <User className="w-5 h-5 text-primary-600" />
              </div>

              <h2 className="text-xl font-semibold text-textPrimary">
                Informations Personnelles
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />

                <div>
                  <p className="text-sm text-gray-500">
                    Email
                  </p>

                  {client.email ? (
                    <a
                      href={`mailto:${client.email}`}
                      className="font-medium hover:text-primary-600"
                    >
                      {client.email}
                    </a>
                  ) : (
                    <p className="font-medium">
                      Non renseigné
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />

                <div>
                  <p className="text-sm text-gray-500">
                    Téléphone
                  </p>

                  {client.phone ? (
                    <a
                      href={`tel:${client.phone}`}
                      className="font-medium hover:text-primary-600"
                    >
                      {client.phone}
                    </a>
                  ) : (
                    <p className="font-medium">
                      Non renseigné
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Cake className="w-5 h-5 text-gray-400" />

                <div>
                  <p className="text-sm text-gray-500">
                    Date de naissance
                  </p>

                  <p className="font-medium">
                    {formatDate(client.dateOfBirth)}

                    {clientAge !== null && (
                      <>
                        {' '}
                        ({clientAge} ans)
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Badge className="w-5 h-5 text-gray-400" />

                <div>
                  <p className="text-sm text-gray-500">
                    Statut
                  </p>

                  <div className="mt-1">
                    {getStatusBadge(client.status)}
                  </div>
                </div>
              </div>

              {clientIsMinor === true && (
                <div className="flex items-center space-x-3 md:col-span-2">
                  <Shield className="w-5 h-5 text-amber-600" />

                  <div>
                    <p className="text-sm text-gray-500">
                      Majorité
                    </p>

                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800 border border-amber-200">
                      Client mineur
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {client.address &&
            (client.address.street ||
              client.address.city ||
              client.address.postalCode ||
              client.address.country) && (
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-accent-100 rounded-lg">
                    <MapPin className="w-5 h-5 text-accent-600" />
                  </div>

                  <h2 className="text-xl font-semibold text-textPrimary">
                    Adresse
                  </h2>
                </div>

                <div className="space-y-2">
                  {client.address.street && (
                    <p className="font-medium text-textPrimary">
                      {client.address.street}
                    </p>
                  )}

                  {client.address.city && (
                    <p className="text-textSecondary">
                      {client.address.postalCode &&
                        `${client.address.postalCode} `}

                      {client.address.city}
                    </p>
                  )}

                  {client.address.country && (
                    <p className="text-textSecondary">
                      {client.address.country}
                    </p>
                  )}
                </div>
              </div>
            )}


          {client.identityDocument &&
            (client.identityDocument.number ||
              client.identityDocument.fileUrl) && (
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>

                  <h2 className="text-xl font-semibold text-textPrimary">
                    Document d'identité
                  </h2>
                </div>

                {client.identityDocument.fileUrl && (
                  <div className="mb-4 max-w-xs">
                    <DocumentThumbnail
                      src={client.identityDocument.fileUrl}
                      mimeType={
                        client.identityDocument.fileMimeType
                      }
                      label={
                        client.identityDocument.fileName ||
                        'Document'
                      }
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      Type
                    </p>

                    <p className="font-medium">
                      {getDocumentTypeLabel(
                        client.identityDocument.type,
                      )}
                    </p>
                  </div>

                  {client.identityDocument.number && (
                    <div>
                      <p className="text-sm text-gray-500">
                        Numéro
                      </p>

                      <p className="font-medium">
                        ***
                        {client.identityDocument.number.slice(-4)}
                      </p>
                    </div>
                  )}

                  {client.identityDocument.issueDate && (
                    <div>
                      <p className="text-sm text-gray-500">
                        Date d'émission
                      </p>

                      <p className="font-medium">
                        {formatDate(
                          client.identityDocument.issueDate,
                        )}
                      </p>
                    </div>
                  )}

                  {client.identityDocument.expiryDate && (
                    <div>
                      <p className="text-sm text-gray-500">
                        Date d'expiration
                      </p>

                      <p className="font-medium">
                        {formatDate(
                          client.identityDocument.expiryDate,
                        )}
                      </p>
                    </div>
                  )}

                  {client.identityDocument.issuePlace && (
                    <div>
                      <p className="text-sm text-gray-500">
                        Lieu d'émission
                      </p>

                      <p className="font-medium">
                        {client.identityDocument.issuePlace}
                      </p>
                    </div>
                  )}

                  {client.identityDocument.issuingAuthority && (
                    <div>
                      <p className="text-sm text-gray-500">
                        Autorité émettrice
                      </p>

                      <p className="font-medium">
                        {
                          client.identityDocument
                            .issuingAuthority
                        }
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

          {idPhotos.length > 0 && (
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>

                <h2 className="text-xl font-semibold text-textPrimary">
                  Photos des documents
                </h2>
              </div>

              <IdPhotoGallery
                photos={idPhotos}
              />
            </div>
          )}


          {client.emergencyContact &&
            client.emergencyContact.name && (
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg">
                    <Heart className="w-5 h-5 text-red-600" />
                  </div>

                  <h2 className="text-xl font-semibold text-textPrimary">
                    Contact d'urgence
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-500">
                      Nom
                    </p>

                    <p className="font-medium">
                      {client.emergencyContact.name}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">
                      Relation
                    </p>

                    <p className="font-medium">
                      {client.emergencyContact.relationship ||
                        'Non renseignée'}
                    </p>
                  </div>

                  {client.emergencyContact.phone && (
                    <div>
                      <p className="text-sm text-gray-500">
                        Téléphone
                      </p>

                      <a
                        href={`tel:${client.emergencyContact.phone}`}
                        className="font-medium hover:text-primary-600"
                      >
                        {client.emergencyContact.phone}
                      </a>
                    </div>
                  )}

                  {client.emergencyContact.email && (
                    <div>
                      <p className="text-sm text-gray-500">
                        Email
                      </p>

                      <a
                        href={`mailto:${client.emergencyContact.email}`}
                        className="font-medium hover:text-primary-600 break-all"
                      >
                        {client.emergencyContact.email}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>

        <div className="lg:col-span-2 space-y-8">

          {client.legalGuardian && (
            <div className="card border border-purple-200">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold text-textPrimary">
                      Tuteur Légal
                    </h2>

                    {clientIsMinor === true && (
                      <p className="text-sm text-purple-600 mt-0.5">
                        Responsable du client mineur
                      </p>
                    )}
                  </div>
                </div>

                {clientIsMinor === true && (
                  <span className="hidden sm:inline-flex rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-800">
                    Informations importantes
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500">
                    Nom complet
                  </p>

                  <p className="font-medium">
                    {client.legalGuardian.firstName}{' '}
                    {client.legalGuardian.lastName}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">
                    Relation
                  </p>

                  <p className="font-medium">
                    {getGuardianRelationshipLabel(
                      client.legalGuardian.relationshipToMinor,
                      client.legalGuardian.relationshipDetails,
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">
                    Email
                  </p>

                  {client.legalGuardian.email ? (
                    <a
                      href={`mailto:${client.legalGuardian.email}`}
                      className="font-medium hover:text-primary-600 break-all"
                    >
                      {client.legalGuardian.email}
                    </a>
                  ) : (
                    <p className="font-medium text-gray-500">
                      Non renseigné
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-sm text-gray-500">
                    Téléphone
                  </p>

                  {client.legalGuardian.phone ? (
                    <a
                      href={`tel:${client.legalGuardian.phone}`}
                      className="font-medium hover:text-primary-600"
                    >
                      {client.legalGuardian.phone}
                    </a>
                  ) : (
                    <p className="font-medium text-gray-500">
                      Non renseigné
                    </p>
                  )}
                </div>

                {client.legalGuardian.identityDocument && (
                  <>
                    <div>
                      <p className="text-sm text-gray-500">
                        Type de pièce d'identité
                      </p>

                      <p className="font-medium">
                        {getDocumentTypeLabel(
                          client.legalGuardian
                            .identityDocument.type,
                        )}
                      </p>
                    </div>

                    {client.legalGuardian.identityDocument
                      .number && (
                      <div>
                        <p className="text-sm text-gray-500">
                          Numéro
                        </p>

                        <p className="font-medium">
                          ***
                          {client.legalGuardian.identityDocument.number.slice(
                            -4,
                          )}
                        </p>
                      </div>
                    )}
                  </>
                )}

                {client.legalGuardian.identityDocument
                  ?.fileUrl && (
                  <div className="md:col-span-2 max-w-xs">
                    <p className="text-sm text-gray-500 mb-2">
                      Pièce d'identité du tuteur
                    </p>

                    <DocumentThumbnail
                      src={
                        client.legalGuardian
                          .identityDocument.fileUrl
                      }
                      mimeType={
                        client.legalGuardian
                          .identityDocument.fileMimeType
                      }
                      label={
                        client.legalGuardian
                          .identityDocument.fileName ||
                        'Document'
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {client.preferences && (
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>

                <h2 className="text-xl font-semibold text-textPrimary">
                  Préférences
                </h2>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-2">
                    Méthode de communication
                  </p>

                  <p className="font-medium capitalize">
                    {client.preferences.communicationMethod ||
                      'Email'}
                  </p>
                </div>

                {client.preferences.privacySettings && (
                  <div>
                    <p className="text-sm text-gray-500 mb-3">
                      Paramètres de confidentialité
                    </p>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">
                          Photographie
                        </span>

                        {client.preferences.privacySettings
                          .allowPhotography ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm">
                          Galerie
                        </span>

                        {client.preferences.privacySettings
                          .allowGalleryUse ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm">
                          Témoignages
                        </span>

                        {client.preferences.privacySettings
                          .allowTestimonials ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm">
                          Marketing
                        </span>

                        {client.preferences.privacySettings
                          .allowMarketing ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===================================================== */}
          {/* CONSENTEMENTS */}
          {/* ===================================================== */}

          {client.consentLogs &&
            client.consentLogs.length > 0 && (
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-lg">
                    <FileText className="w-5 h-5 text-indigo-600" />
                  </div>

                  <h2 className="text-xl font-semibold text-textPrimary">
                    Consentements
                  </h2>
                </div>

                <div className="space-y-3">
                  {client.consentLogs.map((consent) => (
                    <div
                      key={consent.id}
                      className="flex items-center justify-between gap-3 text-sm border-b border-gray-100 pb-2"
                    >
                      <div>
                        <p className="font-medium">
                          {getConsentTypeLabel(
                            consent.consentType,
                          )}
                        </p>

                        <p className="text-xs text-gray-500">
                          {formatDate(consent.timestamp)}
                        </p>
                      </div>

                      {consent.consentGiven ? (
                        <CheckCircle className="w-5 h-5 shrink-0 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 shrink-0 text-red-500" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* ===================================================== */}
          {/* INFORMATIONS DU COMPTE */}
          {/* ===================================================== */}

          <div className="card">
            <h2 className="text-lg font-semibold text-textPrimary mb-4">
              Informations du compte
            </h2>

            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-600">
                  Créé le
                </span>

                <span className="font-medium text-right">
                  {formatDate(client.createdAt)}
                </span>
              </div>

              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-600">
                  Dernière modification
                </span>

                <span className="font-medium text-right">
                  {formatDate(client.updatedAt)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}